import { Grid, useEventCallback } from '@mui/material';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';
import { Fragment, memo } from 'react';
import ForeignStateList, {
  ForeignStateVM,
  ID_ALTRO_STATO,
} from 'shell/components/shared/foreign-state-list';
import { FormikErrors, FormikTouched } from 'formik';
import PmiTextField from 'shell/components/shared/text-field';
import { noop } from 'shell/utils/noop';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { DateTime } from 'luxon';
import ListConsulate from '../section/list/list-consulate';
import { ItemVM } from 'shell/view-models/item';
import { ID_ITALIAN_NATION } from 'shell/constants/id-italian-nation';

interface ForeignComponentProps {
  disabledComponent?: boolean;
  values?: ForeignForm;
  errors?: FormikErrors<ForeignForm>;
  touched?: FormikTouched<ForeignForm>;
  onChange: (key: keyof ForeignForm, value: ForeignForm[keyof ForeignForm]) => void;
  onChanges: (values: ForeignForm) => void;
  labelState?: string;
  labelMunicipalityAire?: string;
  labelProvinceAire?: string;
  required?: boolean;
  isBirth?: boolean;
  isResidence?: boolean;
  disabledAire?: boolean;
  isWithLocationForeign?: boolean;
  isWithConsulate?: boolean;
  fromDate?: DateTime | null;
  isNotRequiredConsulate?: boolean;
}

const ForeignComponent = ({
  disabledComponent,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  labelState = 'Stato estero di nascita',
  labelMunicipalityAire = 'Comune di iscrizione AIRE',
  labelProvinceAire = 'Provincia di iscrizione AIRE',
  required = false,
  isBirth = false,
  isResidence = false,
  disabledAire = false,
  isWithLocationForeign = false,
  isWithConsulate = false,
  isNotRequiredConsulate = false,
  fromDate,
}: ForeignComponentProps): React.ReactElement => {
  const handleForeignStateListChange = useEventCallback(
    (state: ForeignStateVM | null | undefined) => {
      onChanges({
        state,
        otherState: '',
      });
    }
  );

  const handleMunicipalityAire = useEventCallback(
    (municipalityAireAire: MunicipalityAnprVM | null) => {
      onChange('municipalityAire', municipalityAireAire);
    }
  );

  const onChangeConsulate = useEventCallback((item?: ItemVM) => {
    onChange('consulate', item);
  });

  return (
    <Grid container spacing={2} item style={{ paddingTop: '0px' }}>
      <Grid item xs={12} md={4}>
        <ForeignStateList
          value={values?.state || null}
          onChange={handleForeignStateListChange}
          disabled={disabledComponent}
          label={labelState}
          required={required}
          id={`foreignState${labelState}`}
          error={
            (touched?.state as unknown as FormikTouched<ForeignStateVM>) &&
            Boolean(errors?.state as FormikErrors<ForeignStateVM>)
          }
        />
      </Grid>
      {values?.state?.code?.toString() === ID_ALTRO_STATO && !isResidence ? (
        <Grid item xs={12} md={4}>
          <PmiTextField
            aria-label="Altro stato"
            label="Altro stato"
            id="otherState"
            name="otherState"
            fullWidth
            type="text"
            variant="outlined"
            disabled={disabledComponent}
            value={values?.otherState || ''}
            onChange={(event) => {
              onChange('otherState', event.target.value);
            }}
          />
        </Grid>
      ) : undefined}
      {isBirth ? (
        <Grid item xs={12} md={4}>
          <PmiTextField
            aria-label="Località estera di nascita"
            label="Località estera di nascita"
            id="locationForeignBirth"
            name="locationForeignBirth"
            fullWidth
            disabled={disabledComponent}
            type="text"
            variant="outlined"
            value={values?.locationForeignBirth || ''}
            onChange={(event) => {
              onChange('locationForeignBirth', event.target.value);
            }}
          />
        </Grid>
      ) : undefined}
      {isResidence ? (
        <Fragment>
          <Grid item xs={12} md={3}>
            <MunicipalityList
              label={labelMunicipalityAire}
              value={values?.municipalityAire || null}
              onChange={handleMunicipalityAire}
              disabled={disabledComponent || disabledAire}
              fromDate={fromDate}
              error={
                (touched?.municipalityAire as unknown as FormikTouched<MunicipalityAnprVM>) &&
                Boolean(errors?.municipalityAire as FormikErrors<MunicipalityAnprVM>)
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              aria-label={labelProvinceAire}
              label={labelProvinceAire}
              id="provinceAire"
              name="provinceAire"
              fullWidth
              type="text"
              variant="outlined"
              value={values?.municipalityAire?.province || ''}
              onChange={noop}
              disabled
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isWithLocationForeign ? (
        <Grid item xs={12} md={4}>
          <PmiTextField
            label="Località estera"
            aria-label="Località estera"
            id="locationForeign"
            name="locationForeign"
            fullWidth
            disabled={disabledComponent}
            type="text"
            variant="outlined"
            value={values?.locationForeign || ''}
            onChange={(event) => {
              onChange('locationForeign', event.target.value);
            }}
          />
        </Grid>
      ) : undefined}
      {isWithConsulate ? (
        <Grid item xs={12} md={4}>
          <ListConsulate
            value={values?.consulate}
            onChange={onChangeConsulate}
            error={touched?.consulate && Boolean(errors?.consulate)}
            disabled={
              disabledComponent ||
              Number.parseInt(values?.state?.code?.toString() || '', 10) === ID_ITALIAN_NATION
            }
            codiceMae={values?.state?.codiceMae}
            required={!isNotRequiredConsulate}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

ForeignComponent.displayName = 'Foreign generality component';

const Foreign = memo(ForeignComponent);

export default Foreign;
